import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/20/solid'
import { ComponentProps, ReactNode } from 'react'

import cn from '@/utilities/cn'

type BannerProps = {
  variant: 'success' | 'warning' | 'error' | 'info'
  children: ReactNode
  onClose?: () => void
  customIcon?: ReactNode
}

export default function Banner({
  variant,
  children,
  onClose,
  className,
  customIcon,
  ...rest
}: BannerProps & ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        {
          'bg-green-50': variant === 'success',
          'bg-yellow-50': variant === 'warning',
          'bg-red-50': variant === 'error',
          'bg-blue-50': variant === 'info'
        },
        'rounded-md p-4',
        className
      )}
      {...rest}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {customIcon || (
            <>
              {variant === 'success' && <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />}
              {variant === 'warning' && (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              )}
              {variant === 'error' && <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />}
              {variant === 'info' && <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />}
            </>
          )}
        </div>
        <div className="ml-3">
          <p
            className={cn(
              {
                'text-green-800': variant === 'success',
                'text-yellow-700': variant === 'warning',
                'text-red-700': variant === 'error',
                'text-blue-700': variant === 'info'
              },
              'text-sm font-medium'
            )}
          >
            {children}
          </p>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={cn(
                  {
                    'bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50':
                      variant === 'success',
                    'bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50':
                      variant === 'warning',
                    'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50':
                      variant === 'error',
                    'bg-blue-50 text-blue-500 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50':
                      variant === 'info'
                  },
                  'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2'
                )}
                onClick={() => onClose?.()}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
